import type { Column } from "@shared/types/data-table"
import type { PricingTable } from "@/features/subscriptions/types/pricing-table/pricing-table.types"
import type { ColumnManagementConfig } from "@shared/lib/tables/utils/column-management"
import {
  createTextColumn,
  createStatusColumn,
  createCustomColumn,
  createDateColumn,
} from "./column-factories"
import { ExternalLink } from "lucide-react"

function fmt(n: number) {
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`
  return n.toLocaleString()
}

export const PRICING_TABLE_COLUMNS: Record<string, Column<PricingTable>> = {
  name: createTextColumn<PricingTable>("name", "Name", 200, "font-medium"),
  status: createStatusColumn<PricingTable>("pricing-table"),
  plans: createCustomColumn<PricingTable>(
    "plans",
    "Plans",
    180,
    (item: PricingTable) => (
      <span className="text-sm text-muted-foreground whitespace-nowrap">
        {item.plans.map(p => p.name).join(' / ')}
      </span>
    )
  ),
  embeds: createCustomColumn<PricingTable>(
    "embeds",
    "Embed",
    180,
    (item: PricingTable) => (
      item.embeds.length > 0 ? (
        <span className="flex items-center gap-1 text-sm whitespace-nowrap">
          {item.embeds.map((e, i) => (
            <span key={e.url} className="inline-flex items-center">
              {i > 0 && <span className="text-muted-foreground mx-0.5">/</span>}
              <a
                href={e.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline inline-flex items-center gap-0.5"
                onClick={(ev) => ev.stopPropagation()}
              >
                {e.location}
                <ExternalLink className="h-3 w-3 flex-shrink-0" />
              </a>
            </span>
          ))}
        </span>
      ) : (
        <span className="text-sm text-muted-foreground">—</span>
      )
    )
  ),
  viewsLast30d: createCustomColumn<PricingTable>(
    "viewsLast30d",
    "Views (30d)",
    120,
    (item: PricingTable) => (
      <span className="text-sm font-medium">
        {item.status !== 'archived' ? fmt(item.viewsLast30d) : '—'}
      </span>
    )
  ),
  conversions: createCustomColumn<PricingTable>(
    "conversions",
    "Conversions",
    120,
    (item: PricingTable) => (
      <span className="text-sm font-medium text-green-700">
        {item.status !== 'archived' ? fmt(item.conversions) : '—'}
      </span>
    )
  ),
  conversionRate: createCustomColumn<PricingTable>(
    "conversionRate",
    "Conv. rate",
    100,
    (item: PricingTable) => (
      item.status === 'active' ? (
        <span className={`text-sm font-medium ${
          item.conversionRate >= 3 ? 'text-green-600' : item.conversionRate >= 1 ? 'text-amber-600' : 'text-muted-foreground'
        }`}>
          {item.conversionRate.toFixed(1)}%
        </span>
      ) : (
        <span className="text-sm text-muted-foreground">—</span>
      )
    )
  ),
  updatedAt: createDateColumn<PricingTable>("updatedAt", "Last updated", 150, false),
}

const PRICING_TABLE_STATUS_CONFIG = {
  all: ['name', 'status', 'plans', 'embeds', 'viewsLast30d', 'conversions', 'conversionRate', 'updatedAt'],
  active: ['name', 'status', 'plans', 'embeds', 'viewsLast30d', 'conversions', 'conversionRate', 'updatedAt'],
  draft: ['name', 'status', 'plans', 'embeds', 'updatedAt'],
  archived: ['name', 'status', 'plans', 'updatedAt'],
} as const

export const getPricingTableColumns = (status: string = 'all'): Column<PricingTable>[] => {
  const keys = PRICING_TABLE_STATUS_CONFIG[status as keyof typeof PRICING_TABLE_STATUS_CONFIG] || PRICING_TABLE_STATUS_CONFIG.all
  return keys.map(key => PRICING_TABLE_COLUMNS[key]).filter(Boolean)
}

export const getPricingTableColumnConfig = (status: string = 'all'): ColumnManagementConfig[] => {
  const visibleKeys: Set<string> = new Set(PRICING_TABLE_STATUS_CONFIG[status as keyof typeof PRICING_TABLE_STATUS_CONFIG] || PRICING_TABLE_STATUS_CONFIG.all)
  return Object.entries(PRICING_TABLE_COLUMNS).map(([key, col], index) => ({
    id: col.key,
    label: typeof col.header === 'string' ? col.header : col.key,
    visible: visibleKeys.has(key),
    required: ['name', 'status'].includes(key),
    order: index
  }))
}
